<script setup lang="ts">
import { RouterView } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useLocalitiesStore } from '@/stores/localities'
import { onMounted, ref, computed } from 'vue'
import { Switch } from '@headlessui/vue'
import PushNotificationStatus from '@/components/PushNotificationStatus.vue'
import LocalitySwitcher from '@/components/LocalitySwitcher.vue'

const authStore = useAuthStore()
const localitiesStore = useLocalitiesStore()

// Nudge banner state
const nudgeDismissed = ref(false)

const showNudgeBanner = computed(() => {
  const user = authStore.user
  if (!user || !authStore.isAuthenticated) return false
  if (user.preferredMeetingType1?.id) return false
  if (nudgeDismissed.value) return false
  return localStorage.getItem(`prefs-nudge-dismissed-${user.id}`) !== 'true'
})

function dismissNudge() {
  const userId = authStore.user?.id
  if (userId) {
    localStorage.setItem(`prefs-nudge-dismissed-${userId}`, 'true')
  }
  nudgeDismissed.value = true
}

async function handleLogout() {
  await authStore.logout()
}

onMounted(async () => {
  authStore.initializeDarkMode()
  await authStore.init()

  const localityName = localitiesStore.selectedLocality?.name || authStore.user?.locality?.name
  if (authStore.isAuthenticated && localityName) {
    // set Title of the page
    document.title = 'Meeting Scheduler - ' + localityName
  }
})
</script>

<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900">
    <!-- Navigation Header - Only show when authenticated -->
    <nav v-if="authStore.isAuthenticated" class="bg-white dark:bg-gray-800 shadow">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between h-16">
          <div class="flex">
            <!-- Logo/Brand -->
            <div class="flex-shrink-0 flex items-center">
              <h1 class="text-xl font-bold text-gray-900 dark:text-white">
                Meeting Scheduler
              </h1>
              <LocalitySwitcher />
              <span
                v-if="!authStore.user?.isGlobalAdmin && (localitiesStore.selectedLocality || authStore.user?.locality)"
                class="ml-2 text-gray-600 dark:text-gray-400"
              >
                - {{ localitiesStore.selectedLocality?.name || authStore.user?.locality?.name }}
              </span>
            </div>

            <!-- Navigation Links -->
            <div class="hidden sm:ml-6 sm:flex sm:space-x-8">
              <RouterLink
                v-if="authStore.isAuthenticated"
                :to="`/localities/${localitiesStore.selectedLocalityId}/meetings`"
                class="inline-flex items-center px-1 pt-1 text-gray-900 dark:text-gray-100 text-sm font-medium"
                active-class="border-b-2 border-blue-500"
              >
                Meetings
              </RouterLink>
              <RouterLink
                v-if="
                  authStore.isAuthenticated &&
                  authStore.user?.isLocalityAdmin &&
                  localitiesStore.selectedLocalityId
                "
                :to="`/localities/${localitiesStore.selectedLocalityId}/users`"
                class="inline-flex items-center px-1 pt-1 text-gray-900 dark:text-gray-100 text-sm font-medium"
                active-class="border-b-2 border-blue-500"
              >
                Users
              </RouterLink>
              <RouterLink
                v-if="
                  authStore.isAuthenticated &&
                  authStore.user?.isLocalityAdmin &&
                  localitiesStore.selectedLocalityId
                "
                :to="`/localities/${localitiesStore.selectedLocalityId}/meeting-types`"
                class="inline-flex items-center px-1 pt-1 text-gray-900 dark:text-gray-100 text-sm font-medium"
                active-class="border-b-2 border-blue-500"
              >
                Meeting Types
              </RouterLink>
              <RouterLink
                v-if="
                  authStore.isAuthenticated &&
                  authStore.user?.isLocalityAdmin &&
                  localitiesStore.selectedLocalityId
                "
                :to="`/localities/${localitiesStore.selectedLocalityId}/roster-draft`"
                class="inline-flex items-center px-1 pt-1 text-gray-900 dark:text-gray-100 text-sm font-medium"
                active-class="border-b-2 border-blue-500"
              >
                Roster Draft
              </RouterLink>
              <RouterLink
                v-if="authStore.isAuthenticated && authStore.user?.isGlobalAdmin"
                to="/admin/localities"
                class="inline-flex items-center px-1 pt-1 text-gray-900 dark:text-gray-100 text-sm font-medium"
                active-class="border-b-2 border-blue-500"
              >
                Localities
              </RouterLink>
            </div>
          </div>

          <!-- Right side - User menu -->
          <div class="flex items-center">
            <div class="relative group">
              <div
                class="flex items-center space-x-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <!-- User Icon -->
                <div
                  class="w-8 h-8 rounded-full bg-blue-500 dark:bg-blue-600 flex items-center justify-center text-white"
                >
                  {{ authStore.user?.userName?.[0]?.toUpperCase() || '?' }}
                </div>
                <span class="hidden sm:inline-block">{{ authStore.user?.userName }}</span>
                <!-- Dropdown Arrow -->
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>

              <!-- Dropdown Menu -->
              <div
                class="invisible group-hover:visible opacity-0 group-hover:opacity-100 absolute right-0 mt-2 w-48 py-1 bg-white dark:bg-gray-800 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50 transition-all duration-200 ease-in-out"
              >
                <div
                  class="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 border-b dark:border-gray-700"
                >
                  Signed in as<br />
                  <span class="font-medium">{{ authStore.user?.email }}</span>
                </div>

                <!-- Push Notification Status -->
                <PushNotificationStatus v-if="authStore.isAuthenticated" />

                <RouterLink
                  to="/profile"
                  class="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  Your Profile
                </RouterLink>
                <Switch
                  :modelValue="authStore.isDarkMode"
                  @update:modelValue="authStore.toggleDarkMode"
                  class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                >
                  <span>Dark Mode</span>
                  <div
                    class="relative inline-flex h-6 w-11 items-center rounded-full bg-gray-200 dark:bg-gray-700"
                  >
                    <span
                      class="inline-block h-4 w-4 transform rounded-full bg-white transition"
                      :class="[authStore.isDarkMode ? 'translate-x-6' : 'translate-x-1']"
                    />
                  </div>
                </Switch>
                <button
                  @click="handleLogout"
                  class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  Sign out
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </nav>

    <!-- Mobile menu - Only show when authenticated -->
    <div v-if="authStore.isAuthenticated" class="sm:hidden" id="mobile-menu">
      <div class="pt-2 pb-3 space-y-1">
        <RouterLink
          :to="`/localities/${localitiesStore.selectedLocalityId}/meetings`"
          class="block pl-3 pr-4 py-2 text-base font-medium text-gray-700 dark:text-gray-300"
          active-class="bg-blue-50 dark:bg-blue-900/50 border-l-4 border-blue-500 text-blue-700 dark:text-blue-400"
        >
          Meetings
        </RouterLink>
        <RouterLink
          v-if="authStore.user?.isLocalityAdmin"
          :to="`/localities/${localitiesStore.selectedLocalityId}/users`"
          class="block pl-3 pr-4 py-2 text-base font-medium text-gray-700 dark:text-gray-300"
          active-class="bg-blue-50 dark:bg-blue-900/50 border-l-4 border-blue-500 text-blue-700 dark:text-blue-400"
        >
          Users
        </RouterLink>
        <RouterLink
          v-if="authStore.user?.isLocalityAdmin"
          :to="`/localities/${localitiesStore.selectedLocalityId}/meeting-types`"
          class="block pl-3 pr-4 py-2 text-base font-medium text-gray-700 dark:text-gray-300"
          active-class="bg-blue-50 dark:bg-blue-900/50 border-l-4 border-blue-500 text-blue-700 dark:text-blue-400"
        >
          Meeting Types
        </RouterLink>
        <RouterLink
          v-if="authStore.user?.isLocalityAdmin"
          :to="`/localities/${localitiesStore.selectedLocalityId}/roster-draft`"
          class="block pl-3 pr-4 py-2 text-base font-medium text-gray-700 dark:text-gray-300"
          active-class="bg-blue-50 dark:bg-blue-900/50 border-l-4 border-blue-500 text-blue-700 dark:text-blue-400"
        >
          Roster Draft
        </RouterLink>
        <RouterLink
          v-if="authStore.user?.isGlobalAdmin"
          to="/admin/localities"
          class="block pl-3 pr-4 py-2 text-base font-medium text-gray-700 dark:text-gray-300"
          active-class="bg-blue-50 dark:bg-blue-900/50 border-l-4 border-blue-500 text-blue-700 dark:text-blue-400"
        >
          Localities
        </RouterLink>
      </div>
    </div>

    <!-- Nudge Banner -->
    <div
      v-if="showNudgeBanner"
      class="bg-yellow-50 dark:bg-yellow-900/30 border-l-4 border-yellow-400 p-4"
    >
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        <p class="text-sm text-yellow-800 dark:text-yellow-200">
          You haven't set your meeting type preferences yet. Visit your profile to set them.
        </p>
        <div class="flex items-center space-x-4 ml-4 flex-shrink-0">
          <RouterLink
            to="/profile"
            class="text-sm font-medium text-yellow-800 dark:text-yellow-200 underline hover:text-yellow-900 dark:hover:text-yellow-100"
          >
            Set Preferences
          </RouterLink>
          <button
            @click="dismissNudge"
            class="text-sm font-medium text-yellow-700 dark:text-yellow-300 hover:text-yellow-800 dark:hover:text-yellow-200"
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <RouterView />
  </div>
</template>

<style>
/* Remove default styles and apply Tailwind's preflight */
*,
*::before,
*::after {
  box-sizing: border-box;
  margin: 0;
  font-weight: normal;
}

body {
  display: block;
  min-height: 100vh;
  line-height: 1.5;
  font-family:
    Inter,
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    Roboto,
    Oxygen,
    Ubuntu,
    Cantarell,
    'Fira Sans',
    'Droid Sans',
    'Helvetica Neue',
    sans-serif;
  text-rendering: optimizeLegibility;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* Add dark mode transition */
.dark {
  color-scheme: dark;
}

/* Optional: Add smooth transitions for dark mode changes */
* {
  transition-property: color, background-color, border-color;
  transition-duration: 200ms;
  transition-timing-function: ease-in-out;
}
</style>
